import { ApiClient, ApiError, endpoints } from "@oshimap/api-client";
import { Session, MemoryTokenStore, type TokenStore } from "@oshimap/auth-core";
import { currentUserSchema } from "@oshimap/schemas";
import { store } from "./use-query";

/**
 * Web 토큰 저장 방식은 OD-004 미결이다.
 * 그때까지 localStorage 를 쓰되 **한 곳에만** 두어 교체가 한 파일로 끝나게 한다 (SEC-01).
 */
const KEY = "oshimap.token";

export class WebTokenStore implements TokenStore {
  readonly kind = "web-localstorage";
  async get() { try { return localStorage.getItem(KEY); } catch { return null; } }
  async set(t: string) { try { localStorage.setItem(KEY, t); } catch { /* private mode */ } }
  async clear() { try { localStorage.removeItem(KEY); } catch { /* noop */ } }
}

export const tokenStore: TokenStore =
  typeof localStorage === "undefined" ? new MemoryTokenStore() : new WebTokenStore();

export const api = new ApiClient({
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? "/api/v1",
  getToken: () => tokenStore.get(),
  onUnauthorized: (token) => session.invalidate(token),
  onForbidden: (token) => session.revalidate(token),
});

export const ep = endpoints(api);

export const session = new Session(tokenStore, {
  async fetchMe(token) {
    try {
      const raw = await new ApiClient({
        baseUrl: api.baseUrl,
        getToken: () => token,
      }).requestRaw("/auth/me");
      const parsed = currentUserSchema.safeParse(raw);
      /* 응답은 왔는데 모양이 다르면 토큰 문제가 아니다 — 계약이 바뀐 것이다. */
      return parsed.success ? { kind: "user", user: parsed.data } : { kind: "unavailable" };
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      return status === 401 || status === 403 ? { kind: "invalid" } : { kind: "unavailable" };
    }
  },
  async revoke(token) {
    await endpoints(new ApiClient({ baseUrl: api.baseUrl, getToken: () => token })).logout();
  },
});

/**
 * 로그아웃하면 캐시를 비운다.
 *
 * 화면별 상태를 쓰던 때는 다시 마운트할 때마다 빈 상태에서 시작했으니
 * 이 문제가 없었다. 공유 캐시로 옮기면 다르다 — 로그아웃한 뒤 같은 화면에
 * 들어오면 캐시에 남아 있던 **이전 사용자의** 즐겨찾기·알림·제보가
 * 새로 받아오는 동안 잠깐 보인다. 한 기기를 나눠 쓰는 경우에 실제로 새는 것이다.
 */
store.bindSession(session);
